import { useReducer, useCallback, useMemo, createContext, useContext, type ReactNode } from 'react';

// ─── Inline type definitions (replacing @/services/attendance imports) ───
export interface TaskQuestionResponse {
  question: string;
  expectedKeywords: string[];
}

export interface SummaryQuestionResponse {
  question: string;
}

export type AttendancePhase =
  | 'idle'
  | 'checkin-loading'
  | 'checkin-question'
  | 'checkin-answering'
  | 'checkin-processing'
  | 'checkin-success'
  | 'working'
  | 'checkout-loading'
  | 'checkout-summary'
  | 'checkout-processing'
  | 'checkout-success';

export interface AttendanceState {
  phase: AttendancePhase;
  taskQuestion: TaskQuestionResponse | null;
  summaryQuestion: SummaryQuestionResponse | null;
  checkInResult: CheckInResultData | null;
  checkOutResult: CheckOutResultData | null;
  workStartTime: Date | null;
  currentTime: Date;
  todayAttendance: TodayAttendanceData | null;
  error: string | null;
}

export interface CheckInResultData {
  attendanceId: string;
  gpsPassed: boolean;
  voicePassed: boolean;
  taskUnderstandingScore: number;
  riskScore: number;
  result: string;
}

export interface CheckOutResultData {
  checkoutId: string;
  workMinutes: number;
  summaryScore: number;
  result: string;
}

export interface TodayAttendanceData {
  date: string;
  shiftId?: string;
  jobId?: string;
  checkInTime?: string;
  checkOutTime?: string;
  canCheckIn: boolean;
  canCheckOut: boolean;
  workHours?: number;
  workMinutes?: number;
  estimatedIncome?: number;
}

type AttendanceAction =
  | { type: 'START_CHECKIN' }
  | { type: 'CHECKIN_LOADING' }
  | { type: 'SET_TASK_QUESTION'; question: TaskQuestionResponse }
  | { type: 'SET_ANSWERING' }
  | { type: 'SUBMIT_CHECKIN' }
  | { type: 'CHECKIN_SUCCESS'; result: CheckInResultData; workStartTime: Date }
  | { type: 'CHECKIN_ERROR'; error: string }
  | { type: 'START_WORK' }
  | { type: 'START_CHECKOUT' }
  | { type: 'CHECKOUT_LOADING' }
  | { type: 'SET_SUMMARY_QUESTION'; question: SummaryQuestionResponse }
  | { type: 'SUBMIT_CHECKOUT' }
  | { type: 'CHECKOUT_SUCCESS'; result: CheckOutResultData }
  | { type: 'CHECKOUT_ERROR'; error: string }
  | { type: 'COMPLETE' }
  | { type: 'RESET' }
  | { type: 'UPDATE_TIME'; time: Date }
  | { type: 'SET_TODAY_ATTENDANCE'; data: TodayAttendanceData }
  | { type: 'LOAD_SAVED_STATE'; state: Partial<AttendanceState> }
  | { type: 'CLEAR_ERROR' };

const initialState: AttendanceState = {
  phase: 'idle',
  taskQuestion: null,
  summaryQuestion: null,
  checkInResult: null,
  checkOutResult: null,
  workStartTime: null,
  currentTime: new Date(),
  todayAttendance: null,
  error: null
};

function attendanceReducer(state: AttendanceState, action: AttendanceAction): AttendanceState {
  switch (action.type) {
    case 'START_CHECKIN':
      return { ...state, phase: 'checkin-loading', error: null };

    case 'CHECKIN_LOADING':
      return { ...state, phase: 'checkin-loading' };

    case 'SET_TASK_QUESTION':
      return {
        ...state,
        phase: 'checkin-question',
        taskQuestion: action.question,
        error: null
      };

    case 'SET_ANSWERING':
      return { ...state, phase: 'checkin-answering' };

    case 'SUBMIT_CHECKIN':
      return { ...state, phase: 'checkin-processing', error: null };

    case 'CHECKIN_SUCCESS':
      return {
        ...state,
        phase: 'checkin-success',
        checkInResult: action.result,
        workStartTime: action.workStartTime,
        error: null
      };

    case 'CHECKIN_ERROR':
      return { ...state, phase: 'checkin-question', error: action.error };

    case 'START_WORK':
      return { ...state, phase: 'working' };

    case 'START_CHECKOUT':
      return { ...state, phase: 'checkout-loading', error: null };

    case 'CHECKOUT_LOADING':
      return { ...state, phase: 'checkout-loading' };

    case 'SET_SUMMARY_QUESTION':
      return {
        ...state,
        phase: 'checkout-summary',
        summaryQuestion: action.question,
        error: null
      };

    case 'SUBMIT_CHECKOUT':
      return { ...state, phase: 'checkout-processing', error: null };

    case 'CHECKOUT_SUCCESS':
      return {
        ...state,
        phase: 'checkout-success',
        checkOutResult: action.result,
        error: null
      };

    case 'CHECKOUT_ERROR':
      return { ...state, phase: 'checkout-summary', error: action.error };

    case 'COMPLETE':
      return { ...state, phase: 'checkout-success' };

    case 'RESET':
      return {
        ...initialState,
        todayAttendance: state.todayAttendance,
        currentTime: state.currentTime
      };

    case 'UPDATE_TIME':
      return { ...state, currentTime: action.time };

    case 'SET_TODAY_ATTENDANCE':
      return {
        ...state,
        todayAttendance: action.data,
        workStartTime: action.data.checkInTime && !action.data.checkOutTime
          ? parseTimeToDate(action.data.checkInTime)
          : state.workStartTime,
        phase: action.data.checkInTime && !action.data.checkOutTime
          ? 'working'
          : action.data.checkInTime && action.data.checkOutTime
            ? 'checkout-success'
            : state.phase
      };

    case 'LOAD_SAVED_STATE':
      return { ...state, ...action.state };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    default:
      return state;
  }
}

function parseTimeToDate(timeStr: string): Date {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const now = new Date();
  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes
  );
}

interface AttendanceStateContextValue {
  state: AttendanceState;
  dispatch: React.Dispatch<AttendanceAction>;
}

const AttendanceStateContext = createContext<AttendanceStateContextValue | null>(null);

export function AttendanceStateProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(attendanceReducer, initialState);

  const value = useMemo(() => ({ state, dispatch }), [state]);

  return (
    <AttendanceStateContext.Provider value={value}>
      {children}
    </AttendanceStateContext.Provider>
  );
}

export function useAttendanceState(): AttendanceStateContextValue {
  const context = useContext(AttendanceStateContext);
  if (!context) {
    throw new Error('useAttendanceState must be used within AttendanceStateProvider');
  }
  return context;
}

export function useAttendanceActions() {
  const { state, dispatch } = useAttendanceState();

  const actions = useMemo(() => ({
    startCheckIn: () => dispatch({ type: 'START_CHECKIN' }),
    setCheckInLoading: () => dispatch({ type: 'CHECKIN_LOADING' }),
    setTaskQuestion: (question: TaskQuestionResponse) =>
      dispatch({ type: 'SET_TASK_QUESTION', question }),
    setAnswering: () => dispatch({ type: 'SET_ANSWERING' }),
    submitCheckIn: () => dispatch({ type: 'SUBMIT_CHECKIN' }),
    checkInSuccess: (result: CheckInResultData) =>
      dispatch({ type: 'CHECKIN_SUCCESS', result, workStartTime: new Date() }),
    checkInError: (error: string) => dispatch({ type: 'CHECKIN_ERROR', error }),
    startWork: () => dispatch({ type: 'START_WORK' }),
    startCheckOut: () => dispatch({ type: 'START_CHECKOUT' }),
    setCheckoutLoading: () => dispatch({ type: 'CHECKOUT_LOADING' }),
    setSummaryQuestion: (question: SummaryQuestionResponse) =>
      dispatch({ type: 'SET_SUMMARY_QUESTION', question }),
    submitCheckOut: () => dispatch({ type: 'SUBMIT_CHECKOUT' }),
    checkOutSuccess: (result: CheckOutResultData) =>
      dispatch({ type: 'CHECKOUT_SUCCESS', result }),
    checkOutError: (error: string) => dispatch({ type: 'CHECKOUT_ERROR', error }),
    complete: () => dispatch({ type: 'COMPLETE' }),
    reset: () => dispatch({ type: 'RESET' }),
    updateTime: (time: Date) => dispatch({ type: 'UPDATE_TIME', time }),
    setTodayAttendance: (data: TodayAttendanceData) =>
      dispatch({ type: 'SET_TODAY_ATTENDANCE', data }),
    loadSavedState: (savedState: Partial<AttendanceState>) =>
      dispatch({ type: 'LOAD_SAVED_STATE', state: savedState }),
    clearError: () => dispatch({ type: 'CLEAR_ERROR' })
  }), [dispatch]);

  return { state, actions, dispatch };
}

export function useCurrentPhase() {
  const { state } = useAttendanceState();
  return state.phase;
}

export function useWorkDuration() {
  const { state } = useAttendanceState();
  const { workStartTime, currentTime } = state;

  return useCallback(() => {
    if (!workStartTime) return null;

    const diff = currentTime.getTime() - workStartTime.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return { hours, minutes };
  }, [workStartTime, currentTime]);
}

export { attendanceReducer, initialState };
export type { AttendanceAction };
